import React, { useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface EmailInputProps {
  value: { email?: string };
  onChange: (val: { email: string }) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
}

function validateEmail(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'Email is required';
  
  // Basic email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) return 'Please enter a valid email address';
  
  // Additional validation for common mistakes
  if (trimmed.includes('..')) return 'Email cannot contain consecutive dots';
  if (trimmed.startsWith('.') || trimmed.endsWith('.')) return 'Email cannot start or end with a dot';
  
  // Check for valid TLD (at least 2 characters)
  const parts = trimmed.split('@');
  if (parts.length === 2) {
    const domain = parts[1];
    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) return 'Please enter a valid email domain';
  }
  
  return '';
}

const EmailInput: React.FC<EmailInputProps> = ({ 
  value, 
  onChange,
  disabled,
  registerSubmit,
}) => {
  const email = value?.email || '';

  const [touched, setTouched] = useState<boolean>(false);

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      setTouched(true);
      const v = valueRef.current;
      const emailErr = validateEmail(v?.email || '');
      return !emailErr;
    });
  }, [registerSubmit]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ email: e.target.value });
  };

  const emailError = touched ? validateEmail(email) : '';

  return (
    <div className="space-y-1.5">
      <Label htmlFor="email" className="text-gray-500 font-medium">
        Email Address <span className="text-red-500">*</span>
      </Label>
      <Input
        id="email"
        type="email"
        value={email}
        onChange={handleEmailChange}
        onBlur={() => setTouched(true)}
        placeholder="Enter email address"
        disabled={disabled}
        className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 ${emailError ? 'border-red-400 focus:border-red-500' : ''}`}
      />
      {emailError && (
        <p className="text-xs text-red-500 mt-0.5">{emailError}</p>
      )}
    </div>
  );
};

export default EmailInput;
